<template>
  <MainFormLayout layout="two-column">
    <div>
      <h1
        id="finalCheckReviewTitle"
        class="font-bitter text-3xl font-bold mb-4">
        Final check &amp; review
      </h1>
      <p class="mb-4">
        This information will be passed on to the associate editor.<br>Once you are happy, go to the bottom of the
        form and agree to our Terms and Conditions before submitting.
      </p>
      <div class="mb-20">
        <ViewProofControl />
      </div>
    </div>

    <div class="mb-20">
      <h2 class="font-bitter text-2xl font-bold border-b border-royal-blue-20 pb-4 mb-8">
        Article type
      </h2>
      <h3 class="font-roboto text-xl font-bold mb-2">
        Article type
      </h3>
      <div class="flex items-start bg-faint-grey rounded-lg p-4 font-bold">
        {{ ArticleTypeLabels[store.state.activeSubmission.articleType as keyof typeof ArticleTypeLabels] }}
      </div>
    </div>

    <div class="mb-20">
      <h2 class="font-bitter text-2xl font-bold border-b border-royal-blue-20 pb-4 mb-8">
        Manuscript files
      </h2>
      <FileList />
      <ButtonComponent
        label="Edit files"
        variant="secondary"
        id="editYourFilesBtn"
        data-testid="link-edit-your-files-btn"
        @customClick="editFiles">
        <template #icon>
          <EditIcon />
        </template>
      </ButtonComponent>
    </div>

    <div class="mb-20">
      <h2
        id="articleInformationTitle"
        class="font-bitter text-2xl font-bold border-b border-royal-blue-20 pb-4 mb-8">
        Title &amp; abstract
      </h2>
      <ArticleInfo />
      <ButtonComponent
        label="Edit article information"
        variant="secondary"
        id="editArticleInfoBtn"
        data-testid="link-edit-article-info-btn"
        @customClick="editArticleInfo">
        <template #icon>
          <EditIcon />
        </template>
      </ButtonComponent>
    </div>

    <div class="mb-20">
      <h2 class="font-bitter text-2xl font-bold border-b border-royal-blue-20 pb-4 mb-8">
        Authors
      </h2>
      <AuthorList />
      <ButtonComponent
        label="Edit author information"
        variant="secondary"
        id="editAuthorInfoBtn"
        data-testid="link-edit-author-info-btn"
        @customClick="editAuthorInfo">
        <template #icon>
          <EditIcon />
        </template>
      </ButtonComponent>
    </div>

    <div class="mb-20">
      <h2 class="font-bitter text-2xl font-bold border-b border-royal-blue-20 pb-4 mb-8">
        Reviewers
      </h2>
      <ReviewerList />
      <ButtonComponent
        label="Edit reviewer information"
        variant="secondary"
        id="editReviewerInfoBtn"
        @customClick="editReviewerInfo"
        data-testid="link-edit-reviewer-info-btn">
        <template #icon>
          <EditIcon />
        </template>
      </ButtonComponent>
    </div>

    <section
      id="terms-and-conditions"
      class="mb-12">
      <div class="border-2 border-royal-blue-20 rounded-md p-8 mb-8">
        <h2 class="font-bitter text-2xl font-bold mb-4">
          Terms and conditions
        </h2>
        <p class="mb-4">
          You must agree to the following before submitting your article:
        </p>
        <ul class="list-disc pl-12 pr-4 mb-4">
          <li class="mb-2">
            You have understood and accept our <a
              href="https://rsc.li/oa-terms "
              target="_blank"
              class="underline text-royal-blue-100 hover:text-royal-blue-160 focus:text-royal-blue-160 visited:text-visited"
              id="li-finalReview-openAccessTerms">
              Open Access Terms and Conditions</a> and that upon acceptance of your article for publication you have a legal obligation to pay the article processing charge (APC) in full (unless your institution has an agreement with us to cover APCs, or an eligible discount or waiver is applied).
          </li>
          <li class="mb-2">
            You have read our guidelines on
            <a
              href="https://www.rsc.org/journals-books-databases/journal-authors-reviewers/author-responsibilities/#Safety-and-hazards"
              target="_blank"
              class="underline text-royal-blue-100 hover:text-royal-blue-160 focus:text-royal-blue-160 visited:text-visited"
              id="li-finalReview-safetyAndHazards">
              safety and hazards</a>,
            and that any hazards or risks
            associated with the work have been clearly highlighted and appropriate warnings given.
            Please ensure that any relevant safety precautions or standard codes of practice are
            explicitly cited, or included as supplementary information, as appropriate.
          </li>
          <li class="mb-2">
            You have read our policy on
            <a
              href="https://www.rsc.org/journals-books-databases/author-and-reviewer-hub/authors-information/responsibilities/#dual-use-research"
              target="_blank"
              class="underline text-royal-blue-100 hover:text-royal-blue-160 focus:text-royal-blue-160 visited:text-visited"
              id="li-finalReview-dualUseResearch">
              Dual Use Research of Concern</a>
            (DURC) and any content
            that could be considered to be DURC has been clearly highlighted in your manuscript.
            Please contact
            <a
              href="mailto:chemicalscience@rsc.org?subject=DURC Notification"
              class="underline text-royal-blue-100 hover:text-royal-blue-160 focus:text-royal-blue-160 visited:text-visited"
              id="lnk-finalReview-mail">
              chemicalscience@rsc.org</a>
            if your manuscript includes DURC.
          </li>
          <li class="mb-2">
            You have read our policy on
            <a
              href="https://www.rsc.org/journals-books-databases/journal-authors-reviewers/author-responsibilities/#code-of-conduct"
              target="_blank"
              class="underline text-royal-blue-100 hover:text-royal-blue-160 focus:text-royal-blue-160 visited:text-visited"
              id="li-finalReview-codeOfConduct">
              Conflicts of interest</a>
            and that a conflicts of
            interest statement is included in your manuscript. Please note that this statement is
            required for all submitted manuscripts.
          </li>
          <li class="mb-2">
            You have read our policies on
            <a
              href="https://www.rsc.org/publishing/publish-with-us/publish-a-journal-article/experimental-reporting"
              target="_blank"
              class="underline text-royal-blue-100 hover:text-royal-blue-160 focus:text-royal-blue-160 visited:text-visited"
              id="li-finalReview-humanAndAnimalTesting">
              human and animal testing</a>
            and if your research
            includes live animal or human subjects the required approval statements are included in
            your manuscript.
          </li>
          <li class="mb-2">
            The manuscript has been submitted solely to this journal and is not
            published, in press, or submitted elsewhere.
          </li>
        </ul>
        <div>
          <FormCheckbox
            data-testid="agree-terms-and-conditions"
            inputId="chkTANDC"
            v-model="termsAgreed">
            <template #labelTextLong>
              <label
                for="chkTANDC"
                class="cursor-pointer py-[.5em] pe-[1em] ps-[2.5em] text-royal-blue-160 peer-disabled:cursor-not-allowed peer-disabled:text-graphite">
                Yes, I have read and agree to the terms and conditions above and I acknowledge that there is an article processing charge (APC) associated with publishing in this journal.
              </label>
            </template>
          </FormCheckbox>
        </div>
      </div>

      <NotificationComponent
        headingText="Article processing charge (APC)"
        data-testid="apc-for-chemical-science-notification"
        variant="information"
        class="mb-8">
        <template #body>
          <p class="mb-2">
            Details of the APC for <i>Chemical Science</i> can be found <a
              href="https://www.rsc.org/publishing/open-access/payments-and-funding"
              target="_blank"
              class="underline text-royal-blue-100 hover:text-royal-blue-160 focus:text-royal-blue-160 visited:text-visited">here</a>, alongside information about waivers and discounts.
          </p>

          <p class="mb-2">
            Use our <a
              href="https://rsc.chronoshub.io/?fq="
              target="_blank"
              class="underline text-royal-blue-100 hover:text-royal-blue-160 focus:text-royal-blue-160 visited:text-visited">journal finder</a> to check if your APC is covered by an institutional agreement with us. All authors may apply for a waiver on acceptance.
          </p>
          <p class="mb-2">
            Ad hoc waiver requests will be considered, and authors will be expected to provide some supporting documentation to show they have no access to institutional funds to cover the APC.
          </p>
          <p class="mb-2">
            Corresponding authors who are based in countries classified as Group A and Group B by Research4Life are eligible to receive a full APC waiver.
          </p>
        </template>
      </NotificationComponent>

      <InlineWarningMessage
        v-if="termsAndConditionsErrorMessage"
        :errorMessage="termsAndConditionsErrorMessage"
        errorTestId="terms-and-conditions"
        class="mt-4" />
    </section>

    <PageValidationErrors
      v-if="showErrors"
      :failed-rules="verifyResult.failedRules"
      variant="error">
    </PageValidationErrors>

    <NotificationComponent
      v-if="showSubmissionError"
      headingText="Submission failed"
      data-testid="failed-to-post-submission"
      variant="error"
      class="mb-8">
      <template #body>
        <p>
          Failed to submit your request. Please retry or
          <a
            data-testid="email-customer-service-post-submission"
            href="mailto:manuscriptsupport@rsc.org?subject=RSC%20Submissions%3A%20Submission%20failed"
            class="underline text-royal-blue-100 hover:text-royal-blue-160 focus:text-royal-blue-160 visited:text-visited">email
            customer services</a> if the issue
          persists.
        </p>
      </template>
    </NotificationComponent>

    <div id="pnlPageButtons">
      <ButtonComponent
        :label="buttonText"
        variant="primary"
        data-testid="submit-manuscript"
        id="submitManuscriptButton"
        @customClick="next"
        :is-loading="busy" />
    </div>

    <!-- S1 User Identifier -->
    <iframe
      id="s1_userIdentifier"
      hidden
      title=""
      height="0"
      width="0"
      src="https://mc.manuscriptcentral.com/common/user_frame.html"></iframe>
    <!-- End S1 User Identifier-->
  </MainFormLayout>
</template>

<script setup lang="ts">
import { computed, onMounted, ref, watch } from 'vue'
import { VerifyResult, Reviewer, ReviewerType, ArticleTypeLabels } from '@/types'
import FileList from '../components/review/FileList.vue'
import ArticleInfo from '../components/review/ArticleInfo.vue'
import AuthorList from '../components/review/AuthorList.vue'
import ReviewerList from '../components/review/ReviewerList.vue'
import PageValidationErrors from '../components/common/PageValidationErrors.vue'
import ViewProofControl from '../components/review/ViewProofControl.vue'
import MainFormLayout from '../layouts/MainFormLayout.vue'
import router from '../routes'
import { useStore } from 'vuex'
import { validateAllSteps } from '../services/validation/finalReview'
import { useRoute } from 'vue-router'
import ButtonComponent from '../components/ui-components/ButtonComponent.vue'
import EditIcon from '../components/ui-components/icons/EditIcon.vue'
import FormCheckbox from '../components/ui-components/FormCheckbox.vue'
import { trackEvent } from '../services/gtm'
import InlineWarningMessage from '@/components/common/InlineWarningMessage.vue'
import { useField } from 'vee-validate'
import NotificationComponent from '@/components/ui-components/NotificationComponent.vue'
import { AxiosError } from 'axios'

const store = useStore()
const route = useRoute()
const busy = ref(false)
const canDisplayErrors = ref(false)
const showSubmissionError = ref(false)

function editFiles() {
  router.push('upload-files')
};
function editArticleInfo() {
  router.push('article-info')
};
function editAuthorInfo() {
  router.push('author-info')
};
function editReviewerInfo() {
  router.push('review-editorial')
};

const termsAgreed = computed({
  get: () => {
    return store.state.activeSubmission.termsAgreed
  },
  set: (value) => {
    store.dispatch('updateTermsAgreed', value)
  },
})

const {
  value: termsAgreedValue,
  errorMessage: termsAndConditionsErrorMessage,
  validate: validateTermsAndConditions,
} = useField(
  'input-terms-agreed',
  'terms-and-conditions-required',
  {
    initialValue: termsAgreed,
    validateOnValueUpdate: false,
  },
)

watch(termsAgreed, (newVal) => {
  if (newVal !== termsAgreedValue.value) {
    termsAgreedValue.value = newVal
    validateTermsAndConditions()
  }
})

watch(() => store.state.submissionAttemptCounter, () => {
  validateTermsAndConditions()
})

function finaliseSubmission() {
  if (busy.value) {
    return
  }
  busy.value = true
  store.dispatch('completeSubmission').then(() => {
    trackEvent(getSubmissionDoneEventPayload())
    router.push('done')
  }).catch((error) => {
    showSubmissionError.value = true
    busy.value = false
    trackSubmitSubmissionError(error)
  })
};

const next = async () => {
  await validateAllSteps(store)

  store.commit('incrementSubmissionAttemptCounter')

  if (canProgress.value) {
    finaliseSubmission()
  }
  else {
    canDisplayErrors.value = true
  }
}

const verifyResult = computed(() => {
  const finalCheckVerifyResult: VerifyResult = { valid: false, errors: [], failedRules: {}, field: '', rules: '' }

  const defaultInvalid: VerifyResult = { valid: false, errors: [], failedRules: {}, field: '', rules: '' }

  const verifyResults = [
    (store.getters.getVerifyResultByName('uploadFilesVerifyResult') as VerifyResult | null) ?? defaultInvalid,
    (store.getters.getVerifyResultByName('articleInfoVerifyResult') as VerifyResult | null) ?? defaultInvalid,
    (store.getters.getVerifyResultByName('authorInfoVerifyResult') as VerifyResult | null) ?? defaultInvalid,
    (store.getters.getVerifyResultByName('reviewEditorialVerifyResult') as VerifyResult | null) ?? defaultInvalid,
    (store.getters.getVerifyResultByName('finalReviewVerifyResult') as VerifyResult | null) ?? defaultInvalid,
  ]

  var invalidSubmission = verifyResults.some(function (verifyResult) {
    return verifyResult.valid === false
  })

  if (invalidSubmission) {
    verifyResults.forEach((verifyResult) => {
      if (Object.keys(verifyResult.failedRules).length !== 0) {
        for (const key in verifyResult.failedRules) {
          finalCheckVerifyResult.failedRules[key] = verifyResult.failedRules[key]
        }
      }
    })
  }
  else {
    finalCheckVerifyResult.valid = true
  }

  return finalCheckVerifyResult
})

const canProgress = computed((): boolean => {
  return verifyResult.value.valid && !busy.value
})

const showErrors = computed((): boolean => {
  return Object.keys(verifyResult.value.failedRules).length > 0 && canDisplayErrors.value
})

const buttonText = computed((): string => {
  if (busy.value) {
    return 'Submitting ... '
  }
  return 'Submit manuscript'
})

onMounted(() => {
  store.commit('_setStep', route.meta.step)
  store.commit('setFinalReviewVisited', true)
})

function getSubmissionDoneEventPayload() {
  return {
    event: 'submissionDone',
    category: 'Final Submission Parameters',
    action: 'click',
    peerReviewAnonymisation: store.state.activeSubmission.peerReviewAnonymisation,
    transparentPeerReview: store.state.activeSubmission.transparentPeerReview,
    themedCollectionResponse: store.state.activeSubmission.themedCollectionResponse,
    themedCollectionTitle: store.state.activeSubmission.themedCollectionTitle,
    dasOptionSelected: store.state.das.selectedOption,
    recommendReviewerReasons: (store.state.activeSubmission.reviewers as Reviewer[])
      .filter(reviewer => reviewer.type === ReviewerType.Recommend)
      .map(reviewer => reviewer.reason),
    recommendReviewerCount: (store.state.activeSubmission.reviewers as Reviewer[])
      .filter(reviewer => reviewer.type === ReviewerType.Recommend).length,
    nonPreferredReviewerCount: (store.state.activeSubmission.reviewers as Reviewer[])
      .filter(reviewer => reviewer.type === ReviewerType.Oppose).length,
  }
}

const trackSubmitSubmissionError = (error: AxiosError | unknown) => {
  const status = (error as AxiosError)?.response?.status
  const message = (error as AxiosError)?.message

  trackEvent({
    category: 'Server Errors',
    action: 'error',
    event: 'submitSubmissionError',
    errorType: status ? `HTTP${status}` : 'UnknownError',
    errorDetails: message || 'No error message available',
    submissionId: store.state.activeSubmission.submissionId,
  })
}
</script>
